import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { IMyInfo } from '@/services/user/types';
import { signout } from '@/services/auth';
import StudentSelectionForm from '@/components/StudentSelection';
import { languageOptions } from '@/utils/helpers/language';
import { LanguagesEnum } from '@/utils/enums/Languages';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CustomAvatar } from '../CustomAvatar';
import { checkIsParent } from '@/utils/helpers/common';
import { useTheme } from '../theme-provider';
import { changeLanguage } from '@/services/user';
import {
  GlobeIcon,
  ProfileIcon,
  SettingsIcon,
  MoonIcon,
} from '../CustomIcon/icons';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '../ui/badge';
import { useAuthStore } from '@/store/authStore';

interface UserMenuProps {
  myInfo: IMyInfo;
}

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  condition?: boolean;
  type?: 'default' | 'language' | 'theme';
  content?: React.ReactNode;
}

export const UserMenu = ({ myInfo }: UserMenuProps) => {
  const [isLanguagePopoverOpen, setIsLanguagePopoverOpen] = useState(false);

  const isMobile = useIsMobile();
  const { theme, setTheme } = useTheme();
  const { i18n, t } = useTranslation('translation', {
    keyPrefix: 'pages.userMenu',
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isParent = checkIsParent();

  const currentLanguage =
    (localStorage.getItem('language') as LanguagesEnum) ||
    LanguagesEnum.Turkish;
  const currentLanguageOption = languageOptions.find(
    (lang) => lang.key === currentLanguage
  );

  const clearAuth = useAuthStore((state) => state.clearAuth);

  const { mutate: handleLogout } = useMutation({
    mutationFn: signout,
    onSuccess: () => {
      clearAuth();
      localStorage.clear();
      queryClient.clear();
      navigate('/signin');
    },
  });

  const menuItems: MenuItem[] = [
    {
      icon: ProfileIcon,
      label: t('profile'),
      onClick: () => navigate('/profile'),
    },
    {
      icon: SettingsIcon,
      label: t('password-operations'),
      condition: isParent,
      onClick: () => navigate('/profile?menu=password-operations'),
    },
    {
      icon: SettingsIcon,
      label: t('change-student-password'),
      condition: !isParent,
      onClick: () => navigate('/profile?menu=password-operations'),
    },
    {
      icon: GlobeIcon,
      label: t('language-selection'),
      type: 'language',
      content: (
        <Popover
          open={isLanguagePopoverOpen}
          onOpenChange={setIsLanguagePopoverOpen}
        >
          <PopoverTrigger asChild>
            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-accent group transition-colors">
              <div className="flex items-center gap-2">
                <GlobeIcon
                  className={cn(
                    'text-muted-foreground',
                    isLanguagePopoverOpen && 'text-primary'
                  )}
                />
                <span className="text-sm font-medium">
                  {t('language-selection')}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-muted/50 px-1 py-1 rounded-md border">
                {currentLanguageOption && (
                  <>
                    <span className="text-xs">
                      {currentLanguageOption.label}
                    </span>
                    <img
                      src={currentLanguageOption.flag}
                      alt={currentLanguageOption.label}
                      className="size-3 rounded-full object-cover"
                    />
                  </>
                )}
              </div>
            </div>
          </PopoverTrigger>
          <PopoverContent
            side={isMobile ? 'bottom' : 'left'}
            align="start"
            className="w-48 p-1 mr-1"
          >
            <div className="flex flex-col gap-px">
              {languageOptions.map((lang) => (
                <Button
                  key={lang.key}
                  variant="ghost"
                  className={cn(
                    'justify-start px-3',
                    currentLanguage === lang.key && 'bg-accent'
                  )}
                  onClick={() => handleLanguageChange(lang.key)}
                >
                  <img
                    src={lang.flag}
                    className="size-4 rounded-full object-cover"
                  />
                  <span className="flex-1 text-left text-sm">{lang.label}</span>
                  {currentLanguage === lang.key && (
                    <img
                      src="/icons/check.svg"
                      alt="check"
                      className="size-4"
                    />
                  )}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      ),
    },
    {
      icon: MoonIcon,
      label: t('dark-mode'),
      type: 'theme',
      content: (
        <div className="flex items-center justify-between p-3 hover:bg-accent group transition-colors">
          <div className="flex items-center gap-2">
            <MoonIcon />
            <span className="text-sm">{t('dark-mode')}</span>
          </div>
          <Switch
            checked={theme === 'dark'}
            onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
          />
        </div>
      ),
    },
  ];

  const { mutate: changeLanguageMutation } = useMutation({
    mutationFn: (language: LanguagesEnum) =>
      changeLanguage({ language: language.toString() }),
    onSuccess: (_, language) => {
      localStorage.setItem('language', language.toString());
      i18n.changeLanguage(language.toString());
    },
  });

  const handleLanguageChange = (language: LanguagesEnum) => {
    changeLanguageMutation(language);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full size-8">
          <CustomAvatar
            src={`${import.meta.env.VITE_CDN_URL}/${myInfo.image}`}
            name={`${myInfo.firstname} ${myInfo.lastname}`}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[18.5rem]" align="end" forceMount>
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CustomAvatar
                  src={`${import.meta.env.VITE_CDN_URL}/${myInfo.image}`}
                  name={`${myInfo.firstname} ${myInfo.lastname}`}
                  className="size-9"
                />
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm">
                    {myInfo.firstname} {myInfo.lastname}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-1 truncate max-w-[150px]">
                    {myInfo.studentInfo.email}
                  </p>
                </div>
              </div>
              <Badge
                variant="outline"
                className="text-xs text-blue-500 border-blue-200 bg-blue-50 px-1.5"
              >
                {myInfo.studentInfo.grade}. Sınıf
              </Badge>
            </div>

            {isParent && <StudentSelectionForm />}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {menuItems.map((item) => {
          // condition tanımlı değilse veya true ise göster
          const shouldShow = item.condition === undefined || item.condition;
          if (!shouldShow) return null;

          if (item.type === 'language' || item.type === 'theme') {
            return (
              <div key={item.label} className="rounded-md overflow-hidden">
                {item.content}
              </div>
            );
          }

          return (
            <DropdownMenuItem
              key={item.label}
              className="p-3 cursor-pointer"
              onClick={item.onClick}
            >
              <item.icon className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">{item.label}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <div className="p-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-destructive text-xs font-medium"
            onClick={() => handleLogout()}
          >
            {t('logout')}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
